import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Bell, ArrowRight, Megaphone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { api, fileUrl } from '../lib/api';
import { PageHeader, SectionHeader, Loading, formatDate, SubscribeForm } from '../components/Shared';

export default function WhatsHappening() {
  const [page, setPage] = useState(null);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([api.get('/pages/whats-happening'), api.get('/news?limit=10'), api.get('/events'), api.get('/alerts/active')]).then(([p, n, e, a]) => {
      setPage(p.value?.data || null);
      setNews(n.value?.data || []);
      setEvents(e.value?.data || []);
      setAlerts(a.value?.data || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader kicker="THE KAWARTHA PEOPLE'S DESK" title={page?.title || "What's Happening"} subtitle={page?.subtitle} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <SectionHeader kicker="LATEST" title="News and updates" />
          <div className="space-y-5">
            {news.map((post) => (
              <Link key={post.id} to={`/newsroom/${post.slug}`} className="block group" data-testid="happening-news-card">
                <Card className="card-lift">
                  <CardContent className="p-5 flex gap-4">
                    {post.image_url && <img src={fileUrl(post.image_url)} alt="" className="h-24 w-32 object-cover rounded-lg shrink-0 hidden sm:block" />}
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#C9A84C]">{post.category?.replace('_', ' ')}</p>
                      <p className="font-serif font-bold text-[#1a2744] leading-snug group-hover:underline decoration-[#C9A84C] decoration-2 underline-offset-4">{post.title}</p>
                      {post.dek && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{post.dek}</p>}
                      <p className="text-xs text-slate-500 mt-1.5">{formatDate(post.published_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <Link to="/newsroom" className="inline-flex items-center text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4 mt-5">Visit the Newsroom <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
        </div>
        <div className="lg:col-span-5 space-y-8">
          <div>
            <SectionHeader kicker="ALERTS" title="Active public alerts" />
            {alerts.length === 0 ? (
              <Card><CardContent className="p-5 text-sm text-slate-500 flex items-center gap-2"><Bell className="h-4 w-4" /> No active alerts right now.</CardContent></Card>
            ) : alerts.map((a) => (
              <Card key={a.id} className={`mb-3 ${a.severity === 'urgent' ? 'border-red-300 bg-red-50' : a.severity === 'warning' ? 'border-amber-300 bg-amber-50' : ''}`} data-testid="happening-alert-card">
                <CardContent className="p-4 flex gap-3">
                  <Megaphone className="h-5 w-5 shrink-0 mt-0.5 text-[#1a2744]" />
                  <div>
                    <p className="font-bold text-[#1a2744]">{a.title}</p>
                    {a.message && <p className="text-sm text-slate-700 mt-0.5">{a.message}</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div>
            <SectionHeader kicker="CALENDAR" title="Upcoming events" />
            <div className="space-y-3">
              {events.filter((e) => e.date >= new Date().toISOString().slice(0, 10)).slice(0, 5).map((e) => (
                <Card key={e.id} data-testid="happening-event-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-xs font-bold text-[#C9A84C] uppercase tracking-wider"><CalendarDays className="h-3.5 w-3.5" /> {e.type}</div>
                    <p className="font-serif font-bold text-[#1a2744] mt-1">{e.title}</p>
                    <p className="text-sm text-slate-600">{formatDate(e.date)} · {e.time} · {e.location}</p>
                    {e.description && <p className="text-sm text-slate-500 mt-1">{e.description}</p>}
                  </CardContent>
                </Card>
              ))}
              {events.length === 0 && <p className="text-sm text-slate-500">No events posted yet.</p>}
            </div>
          </div>
          <Card className="bg-[#1a2744] border-0">
            <CardContent className="p-6">
              <p className="font-serif font-black text-white text-lg">Never miss an update</p>
              <p className="text-slate-300 text-sm mt-1 mb-4">Subscribe for alerts and campaign news.</p>
              <SubscribeForm compact testId="happening-subscribe-form" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
